/**
 * ShareableProductPoster.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Web port of the mobile "WhatsApp-Status-grade" product poster.
 *
 * A self-contained, branded marketing poster for a single product. It is shown
 * VISIBLY inside the share modal (so the user sees exactly what they'll post)
 * and simultaneously acts as the capture source for `html-to-image` (the modal
 * snapshots this node to a PNG).
 *
 * Why a baked image instead of a plain link?
 *   WhatsApp Status and chats are visual-first. A plain link reads as spam and
 *   converts poorly. A branded poster — hero photo, bold price, vendor identity,
 *   scannable QR + clean URL — stops the scroll and gives a buyer everything
 *   they need to act, even when the link isn't tappable (e.g. a Status
 *   screenshot).
 *
 * The layout mirrors the React Native `ShareableProductPoster` so both
 * platforms produce a visually identical poster.
 */
import React, { forwardRef } from "react";
import styled from "styled-components";
import { QRCodeSVG } from "qrcode.react";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import optimizeCloudinaryUrl from "../../utils/cloudinaryOptimize";
import LusukuLogo from "../../assets/lusuku-logo.png";
import { formatCurrency, SITE_ORIGIN } from "../../config/market";

// Bare host (no scheme) for compact display, e.g. "lusuku.ug"
const SITE_HOST = SITE_ORIGIN.replace(/^https?:\/\//, "");

// ─── Design system (kept in sync with the mobile poster) ─────────────────────
export const POSTER_W = 360;
export const POSTER_H = Math.round((POSTER_W * 16) / 9); // 9:16 — full-bleed Status

const BRAND = {
  tealTop: "#3A5A63",
  tealBottom: "#1B2F34",
  gold: "#F2C94C",
  ink: "#16252A",
  white: "#FFFFFF",
  whiteSoft: "rgba(255,255,255,0.72)",
};

const formatPrice = (value) => formatCurrency(value);

/**
 * @param {object}  item     Product: { name, image, price, description, vendorName }
 * @param {string}  shopUrl  Canonical, cache-busted shop URL for the QR + link.
 * The forwarded ref points at the capture root (the full poster node).
 */
const ShareableProductPoster = forwardRef(({ item, shopUrl }, ref) => {
  const hasImage = Boolean(item?.image);

  // Full product image with NO crop (c_fit) so nothing is ever cut off — the
  // product is the focus. High-res so it stays sharp when captured upscaled.
  // (The web cloudinary util already omits g_auto for c_fit, so the URL is
  // valid and the image loads.)
  const heroUri = hasImage
    ? optimizeCloudinaryUrl(item.image, {
        width: 900,
        crop: "fit",
        quality: "auto:good",
        dpr: 2,
      })
    : null;

  return (
    <Poster ref={ref}>
      <Glow position="top" />
      <Glow position="bottom" />

      <Content>
        {/* Header */}
        <Header>
          <LogoChip>
            <Logo src={LusukuLogo} alt="Lusuku" />
          </LogoChip>
          <Tag>AVAILABLE NOW</Tag>
        </Header>

        {/* Hero */}
        <HeroWrap>
          {hasImage ? (
            <Hero
              src={heroUri}
              alt={item?.name || "Product"}
              crossOrigin="anonymous"
            />
          ) : (
            <HeroFallback>📦</HeroFallback>
          )}
          <PriceBadge>{formatPrice(item?.price)}</PriceBadge>
        </HeroWrap>

        {/* Title + vendor + description */}
        <TitleBlock>
          <Name>{item?.name || "Featured product"}</Name>
          {item?.vendorName ? (
            <VendorRow>
              <StorefrontOutlinedIcon style={{ fontSize: 15, color: BRAND.gold }} />
              <VendorText>{item.vendorName}</VendorText>
            </VendorRow>
          ) : null}
          {item?.description ? <Desc>{item.description}</Desc> : null}
        </TitleBlock>

        {/* Order card */}
        <OrderCard>
          <QrBox>
            <QRCodeSVG
              value={shopUrl || SITE_ORIGIN}
              size={74}
              fgColor={BRAND.ink}
              bgColor={BRAND.white}
              level="M"
            />
          </QrBox>
          <OrderTextWrap>
            <OrderTitle>Scan to order this Item</OrderTitle>
            <OrderSub>or visit {SITE_HOST}</OrderSub>
            <CtaPill>
              <ShoppingCartOutlinedIcon style={{ fontSize: 14, color: BRAND.ink }} />
              <span>Order on Lusuku</span>
            </CtaPill>
          </OrderTextWrap>
        </OrderCard>
      </Content>
    </Poster>
  );
});

ShareableProductPoster.displayName = "ShareableProductPoster";

export default ShareableProductPoster;

/* ─── Styles ──────────────────────────────────────────────────────────────── */
const Poster = styled.div`
  position: relative;
  width: ${POSTER_W}px;
  height: ${POSTER_H}px;
  border-radius: 18px;
  overflow: hidden;
  background: linear-gradient(150deg, ${BRAND.tealTop} 0%, ${BRAND.tealBottom} 100%);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
`;

const Glow = styled.div`
  position: absolute;
  border-radius: 50%;
  ${({ position }) =>
    position === "top"
      ? `top: -100px; right: -80px; width: 240px; height: 240px;
         background: rgba(242,201,76,0.12);`
      : `bottom: -120px; left: -90px; width: 260px; height: 260px;
         background: rgba(255,255,255,0.06);`}
`;

const Content = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 22px 20px 20px;
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LogoChip = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 25px;
  background: ${BRAND.white};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.22);
`;

const Logo = styled.img`
  width: 46px;
  height: 46px;
  object-fit: contain;
`;

const Tag = styled.div`
  background: ${BRAND.gold};
  color: ${BRAND.ink};
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.8px;
  padding: 5px 10px;
  border-radius: 999px;
`;

const HeroWrap = styled.div`
  position: relative;
  width: 100%;
  height: 260px;
  border-radius: 22px;
  overflow: hidden;
  background: ${BRAND.white};
`;

const Hero = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const HeroFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 64px;
`;

const PriceBadge = styled.div`
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: ${BRAND.gold};
  color: ${BRAND.ink};
  font-size: 19px;
  font-weight: 900;
  letter-spacing: 0.2px;
  padding: 8px 15px;
  border-radius: 14px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
`;

const TitleBlock = styled.div`
  margin-top: 4px;
`;

const Name = styled.div`
  font-size: 24px;
  line-height: 29px;
  font-weight: 900;
  color: ${BRAND.white};
  letter-spacing: 0.2px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const VendorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
`;

const VendorText = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${BRAND.gold};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Desc = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: ${BRAND.whiteSoft};
  margin-top: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const OrderCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${BRAND.white};
  border-radius: 18px;
  padding: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
`;

const QrBox = styled.div`
  background: ${BRAND.white};
  border-radius: 10px;
  padding: 4px;
  line-height: 0;
`;

const OrderTextWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

const OrderTitle = styled.div`
  font-size: 16px;
  font-weight: 900;
  color: ${BRAND.ink};
`;

const OrderSub = styled.div`
  font-size: 12px;
  color: #6b7b80;
  margin-top: 1px;
`;

const CtaPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
  background: ${BRAND.gold};
  color: ${BRAND.ink};
  font-size: 12px;
  font-weight: 800;
  padding: 5px 10px;
  border-radius: 999px;
`;
